import { useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, RefreshCw, Pencil, MessageSquarePlus, AlertTriangle, Info } from '@/components/icons';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { getNdrQueue, ndrAction, type NdrRow, type NdrActionKind, type NdrActionLog } from '@/api/delhivery';

// Delhivery returns a forward shipment after its third failed attempt.
const MAX_ATTEMPTS = 3;

const ACTION: Record<NdrActionKind, { label: string; done: string }> = {
  reattempt: { label: 'Re-attempt', done: 'Re-attempt requested' },
  update: { label: 'Update details', done: 'Details updated' },
  rto: { label: 'Return (RTO)', done: 'Marked to return' },
  note: { label: 'Add note', done: 'Note' },
};

/** Delhivery's verdict on a live action, as a small coloured tag. */
function OutcomeTag({ outcome, message }: { outcome: NdrActionLog['outcome']; message: string | null }) {
  if (!outcome) return null;
  const tone = outcome === 'accepted' ? 'bg-success/15 text-success' : outcome === 'failed' ? 'bg-danger/15 text-danger' : 'bg-warning/15 text-warning';
  const label = outcome === 'accepted' ? 'accepted' : outcome === 'failed' ? 'rejected' : 'processing';
  return <span className={`ml-1 rounded px-1 text-[9px] font-bold uppercase ${tone}`} title={message ?? undefined}>{label}</span>;
}

const fmtWhen = (at: string | null | undefined) =>
  at ? new Date(at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
const fmtDay = (at: string | null | undefined) =>
  at ? new Date(at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysSince = (at: string | null | undefined) => (at ? Math.max(0, Math.floor((Date.now() - Date.parse(at)) / 86_400_000)) : null);

export const NDR_QUERY_KEY = ['shipping-ndr'];

/** Failed-delivery queue: why each parcel wasn't delivered, how many attempts, and what to do next. */
export function NdrQueue() {
  const ndrQ = useQuery({ queryKey: NDR_QUERY_KEY, queryFn: getNdrQueue });
  const rows = ndrQ.data?.rows ?? [];
  const live = ndrQ.data?.live ?? false;
  const [open, setOpen] = useState<{ row: NdrRow; action: NdrActionKind } | null>(null);

  // Reason takes whatever row width is spare (164–420px), so the table fills the card
  // without a gap before the buttons and without scrolling sideways.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [reasonW, setReasonW] = useState(164);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const fit = () => {
      const table = wrap.querySelector('table');
      let box = table?.parentElement ?? null;
      while (box && box !== wrap && getComputedStyle(box).overflowX === 'visible') box = box.parentElement;
      if (!table || !box) return;
      // Too wide → give back the overflow. Otherwise hand Reason the empty space the
      // browser left in the button column (to the left of Re-attempt).
      const overflow = table.scrollWidth - box.clientWidth;
      let spare = -overflow;
      if (overflow <= 1) {
        const td = table.querySelector<HTMLElement>('tbody tr td:last-child');
        const btns = td ? [...td.querySelectorAll('button')] : [];
        if (!td || !btns.length) return;
        const cs = getComputedStyle(td);
        const avail = td.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        spare = Math.floor(avail - (btns[btns.length - 1].getBoundingClientRect().right - btns[0].getBoundingClientRect().left));
      }
      if (Math.abs(spare) > 2) setReasonW((w) => Math.min(420, Math.max(164, w + spare)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [rows.length, reasonW]);

  return (
    <div ref={wrapRef}>
      {ndrQ.data && !live && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] text-[var(--text-secondary)]">
          <Info size={14} className="mt-0.5 shrink-0 text-warning" />
          <span>
            <strong className="text-[var(--text-primary)]">Dry run.</strong> Re-attempt and Update details are recorded here but not sent to Delhivery
            until NDR is switched live — switch it on in Configurations → Delhivery → NDR actions live.
          </span>
        </div>
      )}

      <Table<NdrRow>
        storageKey="shipping-ndr"
        paginated
        title="Failed deliveries (NDR)"
        count={`${rows.length} shipment(s) · oldest first`}
        rows={rows}
        rowKey={(r) => String(r.shipmentId)}
        loading={ndrQ.isFetching && rows.length === 0}
        empty="No failed deliveries. Shipments appear here when Delhivery reports a failed attempt."
        columns={[
          {
            key: 'order', header: 'Order', minWidth: '68px',
            cell: (r) => <span className="font-extrabold text-accent">{r.orderName ?? r.grnNo ?? '—'}</span>,
          },
          {
            key: 'awb', header: 'AWB', minWidth: '118px',
            cell: (r) => <span className="font-mono text-xs font-semibold">{r.awbNo ?? '—'}</span>,
          },
          {
            key: 'customer', header: 'Customer', minWidth: '110px',
            cell: (r) => <span className="mx-auto block max-w-[118px] truncate font-bold" title={r.consignee ?? undefined}>{r.consignee ?? '—'}</span>,
          },
          {
            key: 'phone', header: 'Phone', minWidth: '96px',
            cell: (r) => (r.phone ? <a href={`tel:${r.phone}`} className="font-semibold text-accent hover:underline">{r.phone}</a> : '—'),
          },
          {
            key: 'reason', header: 'Reason', minWidth: `${reasonW}px`, // widened to the spare row width (see reasonW)
            cell: (r) => {
              // Fixed width, one line each — the full text is on hover.
              const detail = [r.reasonCode, r.statusLocation, fmtWhen(r.lastAttemptAt ?? r.statusAt)].filter(Boolean).join(' · ');
              const main = r.reason ?? r.statusDetail ?? '—';
              return (
                <div className="mx-auto flex flex-col text-left" style={{ width: reasonW }} title={`${main}\n${detail}`}>
                  <span className="truncate font-semibold text-[var(--text-primary)]">{main}</span>
                  <span className="truncate text-[10px] text-[var(--text-muted)]">{detail}</span>
                </div>
              );
            },
          },
          {
            key: 'attempts', header: 'Attempts', align: 'center', minWidth: '60px',
            cell: (r) => (
              <span
                className={`font-extrabold tabular-nums ${r.attempts >= MAX_ATTEMPTS - 1 ? 'text-danger' : 'text-warning'}`}
                title={r.attempts >= MAX_ATTEMPTS - 1 ? 'Next failure returns the parcel (RTO)' : undefined}
              >
                {r.attempts || 1} of {MAX_ATTEMPTS}
              </span>
            ),
          },
          {
            key: 'open', header: 'Open since', minWidth: '140px',
            cell: (r) => {
              const since = r.firstNdrAt ?? r.statusAt;
              const d = daysSince(since);
              // One line, e.g. "Today, 23 Oct 2026".
              return (
                <span className="whitespace-nowrap">
                  <span className="font-semibold">{d == null ? '—' : d === 0 ? 'Today' : `${d} day${d === 1 ? '' : 's'}`}</span>
                  {since && <span className="font-semibold">, {fmtDay(since)}</span>}
                </span>
              );
            },
          },
          {
            key: 'value', header: 'Value', align: 'right', minWidth: '80px',
            cell: (r) => (
              <div className="flex flex-col items-end">
                <span className="font-bold tabular-nums">{r.value ? `₹${formatINR(r.value)}` : '—'}</span>
                {r.paymentMode === 'COD' && <span className="text-[10px] font-bold text-warning">COD ₹{formatINR(r.codAmount)}</span>}
              </div>
            ),
          },
          {
            key: 'last', header: 'Last action', minWidth: '90px',
            cell: (r) => {
              const a = r.actions[0];
              if (!a) return <span className="text-[var(--text-muted)]">None yet</span>;
              return (
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {ACTION[a.action]?.done ?? a.action}
                    {a.dryRun && <span className="ml-1 rounded bg-warning/15 px-1 text-[9px] font-bold uppercase text-warning">dry run</span>}
                    <OutcomeTag outcome={a.outcome} message={a.outcomeMessage} />
                  </span>
                  {a.outcome === 'failed' && a.outcomeMessage && <span className="text-[10px] font-semibold text-danger">{a.outcomeMessage}</span>}
                  <span className="text-[10px] text-[var(--text-muted)]">{[a.by, fmtWhen(a.at)].filter(Boolean).join(' · ')}</span>
                  {a.note && <span className="truncate text-[10px] text-[var(--text-secondary)]" title={a.note}>“{a.note}”</span>}
                </div>
              );
            },
          },
          {
            key: 'actions', header: '', align: 'right',
            cell: (r) => (
              // Standard small ERP buttons (same as every other table), kept on one line.
              <div className="flex flex-nowrap justify-end gap-1 [&>button]:whitespace-nowrap [&>button]:!gap-1 [&>button]:!px-2">
                <Button size="sm" variant="primary" icon={<RefreshCw size={12} />} onClick={() => setOpen({ row: r, action: 'reattempt' })}>Re-attempt</Button>
                <Button size="sm" variant="glass" icon={<Pencil size={12} />} onClick={() => setOpen({ row: r, action: 'update' })}>Update</Button>
                <Button size="sm" variant="glass" icon={<RotateCcw size={12} />} onClick={() => setOpen({ row: r, action: 'rto' })}>Return</Button>
                <Button size="sm" variant="glass" icon={<MessageSquarePlus size={12} />} onClick={() => setOpen({ row: r, action: 'note' })}>Note</Button>
              </div>
            ),
          },
        ]}
      />

      {open && <NdrActionModal key={`${open.row.shipmentId}-${open.action}`} row={open.row} action={open.action} live={live} onClose={() => setOpen(null)} />}
    </div>
  );
}

function NdrActionModal({ row, action, live, onClose }: { row: NdrRow; action: NdrActionKind; live: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(row.consignee ?? '');
  const [phone, setPhone] = useState(row.phone ?? '');
  const [address, setAddress] = useState(row.address ?? '');
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () => {
      // Only send what actually changed, so Delhivery isn't asked to "update" untouched fields.
      const changed = action === 'update'
        ? {
          name: name.trim() !== (row.consignee ?? '') ? name : undefined,
          phone: phone.trim() !== (row.phone ?? '') ? phone : undefined,
          address: address.trim() !== (row.address ?? '') ? address : undefined,
        }
        : {};
      return ndrAction(row.shipmentId, { action, ...changed, note: note.trim() || undefined });
    },
    onSuccess: (r) => {
      toast.push(r.message, r.dryRun ? 'info' : r.outcome === 'pending' ? 'warning' : 'success');
      qc.invalidateQueries({ queryKey: NDR_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['shipping-rows'] });
      onClose();
    },
    onError: (e) => {
      toast.push(apiError(e, 'Action failed'), 'error');
      // A Delhivery refusal is still recorded in the history.
      qc.invalidateQueries({ queryKey: NDR_QUERY_KEY });
    },
  });

  const sendsToDelhivery = action === 'reattempt' || action === 'update';
  const intro: Record<NdrActionKind, string> = {
    reattempt: 'Ask Delhivery to try delivering again. Add when the customer is available, if you know.',
    update: 'Correct the customer’s name, phone or address with Delhivery. Change only what is wrong.',
    rto: 'Record that this parcel should come back. Delhivery returns it after its final attempt — use Return in Delhivery One to send it back sooner.',
    note: 'Log a call or update — for example, what the customer said.',
  };
  const canSubmit = action === 'note' ? !!note.trim() : true;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${ACTION[action].label} — ${row.orderName ?? row.awbNo}`}
      size="md"
      footer={(
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant={action === 'rto' ? 'danger' : 'primary'} disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? 'Saving…' : ACTION[action].label}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="rounded-lg border border-[var(--border-color)] px-3 py-2 text-[12px]">
          <div className="font-bold text-[var(--text-primary)]">{row.reason ?? row.statusDetail ?? 'Delivery failed'}</div>
          <div className="text-[11px] text-[var(--text-muted)]">
            AWB {row.awbNo} · attempt {row.attempts || 1} of {MAX_ATTEMPTS}{row.lastAttemptAt ? ` · ${fmtWhen(row.lastAttemptAt)}` : ''}
          </div>
        </div>
        <p className="text-[12px] text-[var(--text-secondary)]">{intro[action]}</p>
        {sendsToDelhivery && !live && (
          <p className="flex items-start gap-1.5 text-[11px] text-warning">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            Dry run — this is recorded in the ERP but not sent to Delhivery.
          </p>
        )}

        {action === 'update' && (
          <>
            <FormGroup label="Customer name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></FormGroup>
            <FormGroup label="Phone" hint="10-digit mobile number"><Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" maxLength={20} /></FormGroup>
            <FormGroup label="Address"><Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} maxLength={400} /></FormGroup>
          </>
        )}
        <FormGroup label={action === 'note' ? 'Note' : 'Note (optional)'} required={action === 'note'}>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder={action === 'reattempt' ? 'e.g. Customer available after 5 pm' : action === 'note' ? 'e.g. Called — will be home on Saturday' : ''}
          />
        </FormGroup>

        {row.actions.length > 0 && (
          <div className="border-t border-[var(--border-color)] pt-2">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">History</div>
            <ol className="flex flex-col gap-1.5">
              {row.actions.map((a, i) => (
                <li key={i} className="text-[11px]">
                  <span className="font-semibold text-[var(--text-primary)]">{ACTION[a.action]?.done ?? a.action}</span>
                  {a.dryRun && <span className="ml-1 text-[9px] font-bold uppercase text-warning">dry run</span>}
                  <OutcomeTag outcome={a.outcome} message={a.outcomeMessage} />
                  {a.outcome === 'failed' && a.outcomeMessage && <span className="block font-semibold text-danger">Delhivery: {a.outcomeMessage}</span>}
                  <span className="text-[var(--text-muted)]"> · {[a.by, fmtWhen(a.at)].filter(Boolean).join(' · ')}</span>
                  {a.details && <span className="block text-[var(--text-muted)]">{Object.entries(a.details).map(([k, v]) => `${k === 'add' ? 'address' : k}: ${v}`).join(' · ')}</span>}
                  {a.note && <span className="block text-[var(--text-secondary)]">“{a.note}”</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Modal>
  );
}
